import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { LoadingButton } from '@mui/lab'
import Box from '@mui/material/Box'
import { AuthFormBox } from './__styled'
import { Input } from '@/components'
import { validateEmail, validatePhoneNumber } from '@/utils/helper'
import { useTheme } from '@mui/material'

interface FormProps {
  error: string
  loading: boolean
  onSubmit: (user: string) => void
}

export default function ForgotForm1({ error, loading, onSubmit }: FormProps) {
  const [user, setUser] = useState('') // Email or Phone number
  const [editing, setEditing] = useState(false)
  const theme = useTheme()

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!user.length) {
      error.user = 'Email or Phone Number is required'
    } else if (!validateEmail(user) && !validatePhoneNumber(user)) {
      error.user = 'Enter Valid Phone/Email'
    }
    return error
  }, [user])

  const onChangeUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser(e.target.value)
    setEditing(true)
  }

  const onSubmitReset = () => {
    if (!formError.user) onSubmit(user)
  }

  return (
    <AuthFormBox>
      <Typography
        fontWeight={700}
        fontSize={{ xs: 32, md: 36 }}
        color='#0f172a'
        sx={{ letterSpacing: '-0.02em', mb: 1.5 }}
      >
        Forgot Your Password?
      </Typography>
      <Typography
        variant='p'
        fontSize={16}
        color='#64748b'
        lineHeight={1.6}
        mt={'12px'}
        mb={3}
        style={{ whiteSpace: 'pre-line' }}
      >
        Enter your registered email or phone number and we'll help you reset it
      </Typography>
      <Input
        value={user}
        placeholder='Email or Phone Number*'
        onChange={onChangeUser}
        errorText={formError.user}
        isError={editing && !!formError.user}
        name='email'
      />
      {!loading && !!error && (
        <Box mt={1}>
          <Typography variant='labelLarge' color={'error.main'}>
            {error}
          </Typography>
        </Box>
      )}
      <LoadingButton
        variant='contained'
        size='large'
        fullWidth
        disabled={!!formError.user}
        onClick={onSubmitReset}
        loading={loading}
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '16px',
          letterSpacing: '-0.01em',
          py: '14px',
          mt: 1,
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
          '&:disabled': {
            background: '#e2e8f0',
            color: '#94a3b8',
            boxShadow: 'none',
          },
        }}
      >
        Submit
      </LoadingButton>
      <Box mt={2} display='flex' justifyContent='center' alignItems={'center'} gap={'6px'}>
        <Typography variant='labelLarge' fontSize={15} color='#64748b' fontFamily={'Inter'}>
          Already have an account?
        </Typography>
        <Link to='/login' style={{ textDecoration: 'none' }}>
          <Typography
            variant='labelLarge'
            fontSize={15}
            sx={{
              color: '#6366f1',
              fontWeight: 600,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: '#5558e3',
              },
            }}
          >
            Sign In
          </Typography>
        </Link>
      </Box>
    </AuthFormBox>
  )
}
